import {
  GetItemPropsOptions,
  UseComboboxReturnValue
} from "downshift";
import * as _ from "lodash";
import debounce from "lodash.debounce";
import * as React from "react";
import styled, { css } from "styled-components";
import { useHotkeys } from "react-hotkeys-hook";

import { InputCss } from "src/library/components/Forms/Input";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { Text } from "src/library/components/Text";
import Icon from "src/library/components/Icon/Icon";
import type { IApiError } from "src/library/utils/errors/apiError";
import { Icons } from "src/library/components/Icon/types";
import type { IProduct } from "src/core/products/api/products.api";
import DropdownItem from "src/library/components/Dropdown/DropdownItem";

import type { ISearchRequest } from "./OrderQuickProductAdd";

interface Props {
  onSearch: (data: ISearchRequest) => void;
  loading: boolean;
  searchResults: IProduct[];
  placeholder: string;
  priceCodeToDisable?: number | null;
  minimumChars?: number;
  error: IApiError | null;
  SearchRowComponent?: React.ComponentType<SearchRowProps>;
  FooterComponent?: React.ComponentType;
  downshiftProps: UseComboboxReturnValue<IProduct>;
  idsToDisable?: number[];
}

export interface SearchRowProps extends GetItemPropsOptions<IProduct> {
  product: IProduct;
  disabled: boolean;
  highlighted: boolean;
}

export interface ProductSearchComponentRef {
  focusOnInput: () => void;
}

const ProductSearchComponent: React.ForwardRefRenderFunction<unknown, Props> = ({ ...props }, ref) => {
  const {
    SearchRowComponent, FooterComponent, downshiftProps
  } = props;

  const [ searchTerm, setSearchTerm ] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);

  const performSearch = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    const minimumChars = typeof props.minimumChars == "number" ? props.minimumChars : 1;

    if (e.target.value.length >= minimumChars) {
      props.onSearch(e.target.value);
      setSearchTerm(e.target.value);
    }
  }, 500);

  const isDisabled = (product: IProduct) => {
    if (props.priceCodeToDisable) {
      const found = _.findIndex(product.prices, price => price.priceCodeId === props.priceCodeToDisable);

      if (found !== -1) {
        return true;
      }
    }

    // check ids to disable
    if (props.idsToDisable && props.idsToDisable.includes(product.id)) {
      return true;
    }

    return false;
  };

  /// hot key for control + s to search
  useHotkeys("ctrl+s", () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  });

  React.useImperativeHandle(ref, () => ({
    focusOnInput() {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }));

  const menuProps = downshiftProps.getMenuProps({}, { suppressRefError: true });

  const inputProps = downshiftProps.getInputProps({
    placeholder: props.placeholder || "Search",
    className: "search-input",
    id: "search",
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      e.persist();
      performSearch(e);
    },
    onFocus: (e: React.ChangeEvent<HTMLInputElement>) => {
      e.persist();
      downshiftProps.openMenu();
    }
  }, { suppressRefError: true });

  return (
    <SearchComponent isOpen={downshiftProps.isOpen}>
      <div className="search-wrapper">
        <Icon
          icon={Icons.SEARCH}
          $fillColor="lightGrey"
        />

        <input
          ref={inputRef}
          {...inputProps}
        />
      </div>

      <div className="search-results-wrapper">
        <ul
          className={`${downshiftProps.isOpen && "active"} search-results`}
          {...menuProps}
        >
          {downshiftProps.isOpen && !!FooterComponent && (
            <FooterWrapper>
              <FooterComponent />
            </FooterWrapper>
          )}

          <HeaderWrapper>
            <Text weight="bold">
              {searchTerm.length ? `Showing results for "${searchTerm}"` : ""}
            </Text>
          </HeaderWrapper>

          {downshiftProps.isOpen && (
            <>
              {props.searchResults.map((item, index) => (
                <div
                  key={`${item.partNumber}-${index}`}
                  {...downshiftProps.getItemProps({
                    item,
                    index
                  })}
                >
                  {SearchRowComponent ? (
                    <SearchRowComponent
                      item={item}
                      disabled={isDisabled(item)}
                      product={item}
                      highlighted={index === downshiftProps.highlightedIndex}
                    />
                  ) : (
                    <DropdownItem
                      disabled={isDisabled(item)}
                      highlighted={index === downshiftProps.highlightedIndex}
                    >
                      <Text className="emphasize">
                        #
                        {item.partNumber}
                      </Text>

                      <Text className="details">
                        {item.description}
                      </Text>
                    </DropdownItem>
                  )}
                </div>
              ))}

              {!props.searchResults.length && !props.error && !props.loading && (
                <DropdownItem>
                  <Text className="details">
                    Nothing found
                  </Text>
                </DropdownItem>
              )}

              {props.error && !props.loading && (
                <DropdownItem>
                  <Text className="error">
                    An unknown error occurred whilst performing your search, please try again.
                  </Text>
                </DropdownItem>
              )}
            </>
          )}
        </ul>
      </div>

      {props.loading && (
        <LoadingSpinner
          className="loading-spinner"
          type="circular"
          position="inline"
          size={20}
        />
      )}
    </SearchComponent>
  );
};

const SearchComponent = styled.div<{ isOpen: boolean }>`
  ${({ theme, isOpen }) => css`
    display: flex;
    flex: 1;
    height: 40px;
    width: 320px;
    max-width: 100%;
    position: relative;

    [role="combobox"] {
      width: 100%;
    }

    .search-wrapper {
      position: relative;

      svg {
        position: absolute;
        left: 5px;
        top: 50%;
        transform: translateY(-50%);
      }

      .search-input {
        ${InputCss};
        width: 240px;
        max-width: 100%;
        border: solid 2px ${theme.colors.lightGrey};
        border-radius: ${theme.borderRadius};
        padding-left: 35px;
        transition: ${theme.transition.quickest};
        font-size: ${theme.fonts.sizes.p};

        &:hover,
        &:focus {
          border-color: ${theme.colors.primaryDark};
          background-color: ${theme.colors.white};
        }

        padding-right: 50px;
        width: 100%;
        height: 100%;
        font-size: ${theme.fonts.sizes.p};

        &:focus {
          ::placeholder {
            color: ${theme.colors[ "primaryDark" ]};
          }
        }
      }
      &:hover {
        svg {
          fill: ${theme.colors.lightGrey};
        }
      }
    }

    .loading-spinner {
      position: absolute;
      top: 50%;
      right: 15px;
      transform: translateY(-50%);
    }

    .search-results-wrapper {
      position: absolute;
      z-index: 1000;
      left: 0;
      overflow-y: visible;
      top: 40px;
      padding-top: 40px;
      min-width: ${isOpen ? "320px" : "0px"};
    }

    .search-results {
      max-height: 320px;
      overflow-y: auto;
      width: 100%;
      border-radius: 0px 0px ${theme.borderRadius} ${theme.borderRadius};
      border-top: 0;
      background: ${theme.colors.white};
      display: none;
      list-style: none;

      &.active {
        display: block;
      }
    }
  `};
`;

const FooterWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    position: absolute;
    top: 100%;
    background: ${theme.colors.white};
    padding: ${theme.spacing.padding.sm};
  `}
`;

const HeaderWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    position: absolute;
    top: 0;
    height: 40px;
    background: ${theme.colors.white};
    padding: ${theme.spacing.padding.sm};
  `}
`;

export default React.forwardRef(ProductSearchComponent);
